import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { authAPI } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Wrench, Video, Info, UserRound, Star } from "lucide-react";
import Logo from "@/components/Logo";
import { deviceTimezone } from "@/lib/datetime";

// "user-star" composite (lucide has no UserStar export)
function UserStarIcon({ className }) {
  return (
    <span className={`relative inline-flex items-center justify-center ${className}`}>
      <UserRound className="h-full w-full" />
      <Star className="absolute -right-1 -top-1 h-2 w-2 fill-current" />
    </span>
  );
}

const CATEGORY_OPTIONS = [
  { key: "founding_member", icon: UserStarIcon, name: "Founding Member", desc: "Free. Launch only, a permanent badge for early supporters.", foundingOnly: true },
  { key: "sim_racing_expert", icon: Wrench, name: "Sim Racing Expert", desc: "Free. Coaches, setup builders and seasoned racers.", needsDetails: true },
  { key: "content_creator", icon: Video, name: "Content Creator", desc: "Free. Streamers, YouTubers and community builders.", needsDetails: true },
];

/**
 * Global sign in / create account modal. Opened via useAuthModal().openAuth("login"|"register").
 * Shares the exact same logo + info + fields as the standalone pages, over a blurred backdrop.
 */
export default function AuthModal() {
  const { open, mode, openAuth, closeAuth } = useAuthModal();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    preferred_currency: "USD",
  });
  const [foundingEnabled, setFoundingEnabled] = useState(true);
  const [category, setCategory] = useState("");
  const [details, setDetails] = useState("");

  // Clear sensitive fields whenever the modal closes, so credentials are not
  // retained on the login form after sign-out.
  useEffect(() => {
    if (!open) {
      setEmail("");
      setPassword("");
      setForm({ email: "", username: "", password: "", first_name: "", last_name: "", preferred_currency: "USD" });
      setCategory("");
      setDetails("");
    }
  }, [open]);

  useEffect(() => {
    if (open && mode === "register") {
      const preselect = new URLSearchParams(location.search).get("category");
      authAPI.getSignupConfig().then((r) => {
        const enabled = !!r.data?.founding_member_enabled;
        setFoundingEnabled(enabled);
        if (preselect) {
          const allowed = ["sim_racing_expert", "content_creator", ...(enabled ? ["founding_member"] : [])];
          if (allowed.includes(preselect)) setCategory(preselect);
        }
      }).catch(() => {});
    }
  }, [open, mode, location.search]);

  const from = location.state?.from?.pathname || "/";
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const categoryOptions = CATEGORY_OPTIONS.filter((o) => !o.foundingOnly || foundingEnabled);
  const needsDetails = category === "sim_racing_expert" || category === "content_creator";

  const handleOpenChange = (o) => {
    if (!o) {
      closeAuth();
      if (location.pathname === "/login" || location.pathname === "/register") {
        navigate("/", { replace: true });
      }
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      const u = data?.user;
      closeAuth();
      if (u && (!u.email_verified || !u.is_approved)) {
        toast.message(!u.email_verified ? "Verify your email to continue" : "Application under review");
        navigate("/pending-approval", { replace: true });
        return;
      }
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && /pending approval|verify/i.test(detail)) {
        toast.message("Account pending", { description: detail });
        closeAuth();
        navigate("/pending-approval", { replace: true });
        return;
      }
      toast.error(detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (foundingEnabled && !category) {
      toast.error("Please choose how you'd like to join");
      return;
    }
    if (needsDetails && !details.trim()) {
      toast.error("Please add a short bio or your online profiles");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        timezone: deviceTimezone(),
        member_category: foundingEnabled ? category : category || "regular",
        member_category_details: needsDetails ? details.trim() : "",
      };
      const data = await register(payload);
      closeAuth();
      toast.success(data.access_token ? "Account created! Check your email to verify." : (data.message || "Account created."));
      navigate("/pending-approval", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === "register";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`max-h-[92vh] overflow-y-auto border-[hsl(240_6%_17%)] bg-[hsl(240_8%_8%)] p-6 ${isRegister ? "sm:max-w-2xl" : "sm:max-w-md"}`}
        data-testid="auth-modal"
      >
        <DialogTitle className="sr-only">{isRegister ? "Create your account" : "Sign in"}</DialogTitle>

        {isRegister ? (
          <>
            <div className="mb-1 flex flex-col items-center text-center">
              <Logo size="md" className="mb-3 flex-col gap-3 sm:flex-row" />
              <h1 className="font-display text-2xl font-bold tracking-wide">Create your account</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We&apos;ll email you a code to verify. New accounts also require a quick admin review before full access.
              </p>
            </div>
            <form onSubmit={doRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="m_first_name">First name</Label>
                  <Input id="m_first_name" value={form.first_name} onChange={upd("first_name")} data-testid="register-firstname-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m_last_name">Last name</Label>
                  <Input id="m_last_name" value={form.last_name} onChange={upd("last_name")} data-testid="register-lastname-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="m_username">Username</Label>
                <Input id="m_username" value={form.username} onChange={upd("username")} required data-testid="register-username-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m_reg_email">Email</Label>
                <Input id="m_reg_email" type="email" value={form.email} onChange={upd("email")} required data-testid="register-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m_reg_password">Password</Label>
                <Input id="m_reg_password" type="password" value={form.password} onChange={upd("password")} required data-testid="register-password-input" />
              </div>

              {/* Membership category */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <Label>How are you joining?{foundingEnabled ? " *" : ""}</Label>
                  <button
                    type="button"
                    onClick={() => { handleOpenChange(false); navigate("/membership"); }}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="register-membership-link"
                  >
                    <Info className="h-3.5 w-3.5" /> Learn about the benefits
                  </button>
                </div>
                <div className="space-y-2">
                  {categoryOptions.map((o) => {
                    const Icon = o.icon;
                    const active = category === o.key;
                    return (
                      <button
                        type="button"
                        key={o.key}
                        onClick={() => setCategory(active && !foundingEnabled ? "" : o.key)}
                        data-testid={`register-category-${o.key}`}
                        className={`flex w-full items-start gap-3 rounded-xl border-2 p-3 text-left transition-colors ${
                          active ? "border-primary bg-primary/10" : "border-border bg-secondary/30 hover:border-primary/50"
                        }`}
                      >
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{o.name}</span>
                          <span className="block text-xs text-muted-foreground">{o.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {!foundingEnabled && (
                  <p className="text-xs text-muted-foreground">Optional. If you don&apos;t pick Expert or Creator, you&apos;ll join as a regular member.</p>
                )}
                {foundingEnabled && (
                  <p className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs font-medium text-amber-300">
                    All options are free. During launch, Experts and Creators also get Founding Member status and perks.
                  </p>
                )}
              </div>

              {needsDetails && (
                <div className="space-y-2">
                  <Label htmlFor="m_cat_details">Short bio and/or your online profiles *</Label>
                  <Textarea
                    id="m_cat_details"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={3}
                    placeholder="Tell us about yourself, link your channels / socials / league, etc."
                    data-testid="register-category-details"
                  />
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="register-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
            </form>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <button type="button" onClick={() => openAuth("login")} className="font-medium text-primary hover:underline" data-testid="auth-switch-login">
                Sign in
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="mb-1 flex flex-col items-center text-center">
              <Logo size="xl" className="mb-3" />
              <p className="mt-2 text-sm text-muted-foreground">The central hub for sim racers</p>
            </div>
            <form onSubmit={doLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="m_email">Email</Label>
                <Input id="m_email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m_password">Password</Label>
                <Input id="m_password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
              <p className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => { handleOpenChange(false); navigate("/forgot-password"); }}
                  className="text-muted-foreground hover:text-primary hover:underline"
                  data-testid="login-forgot-password"
                >
                  Forgot your password?
                </button>
              </p>
            </form>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <button type="button" onClick={() => openAuth("register")} className="font-medium text-primary hover:underline" data-testid="auth-switch-register">
                Create one
              </button>
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
